/**
 * Bundle version reader.
 *
 * Resolves the current bundle's version from /version.json (written
 * at build time by scripts/write-version.mjs). Cached after first
 * read — version doesn't change without a reload.
 *
 * Used by:
 *   - lib/ota.ts → as the `currentVersion` query param when checking
 *     the manifest (so server can decide upToDate without us shipping
 *     bundles redundantly)
 *   - components/AppVersionPill.tsx → footer pill in the avatar dropdown
 *     ("v 0.0.412 (1b90552) · 27/04 14:30")
 *
 * version.json shape (post semver migration):
 *   { version: "0.0.<N>", sha: "<git short sha>", builtAt: ISO }
 * `version` is semver because @capgo's download() requires it; `sha`
 * is kept for traceability. Old bundles may still carry a sha in
 * `version` and no `sha` field — handled gracefully below.
 */

export interface BundleVersion {
  version: string;
  sha?: string;
  builtAt: string;
}

let cached: BundleVersion | null = null;
let inFlight: Promise<BundleVersion> | null = null;

const FALLBACK: BundleVersion = {
  version: 'dev',
  builtAt: new Date().toISOString(),
};

export async function getBundleVersion(): Promise<BundleVersion> {
  if (cached) return cached;
  if (inFlight) return inFlight;

  inFlight = (async () => {
    try {
      // version.json is at the bundle root. cache: 'no-store' + query
      // timestamp = belt+suspenders pra Capacitor WebView nunca devolver
      // a versão do bundle ANTERIOR depois de um swap OTA (causava loop
      // de redownload infinito antes — server via versão velha porque
      // a fetch trazia /version.json cacheado).
      const response = await fetch(`/version.json?_=${Date.now()}`, {
        cache: 'no-store',
      });
      if (!response.ok) throw new Error(`status ${response.status}`);
      const json = (await response.json()) as Partial<BundleVersion>;
      const result: BundleVersion = {
        version: json.version ?? 'dev',
        sha: json.sha,
        builtAt: json.builtAt ?? new Date().toISOString(),
      };
      cached = result;
      return result;
    } catch {
      cached = FALLBACK;
      return FALLBACK;
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
}

/** Synchronous accessor — returns cached version or fallback. Use
 *  after `await getBundleVersion()` once at app boot. */
export function getCachedBundleVersion(): BundleVersion {
  return cached ?? FALLBACK;
}

/** Pretty version string for UI: "v 0.0.412 (1b90552) · 27/04 14:30".
 *  The sha appears as a secondary detail when present (post-migration
 *  bundles); legacy bundles just show their version string. */
export function formatVersion(v: BundleVersion = getCachedBundleVersion()): string {
  if (v.version === 'dev') return 'dev build';
  const date = new Date(v.builtAt);
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const hh = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  const shaSuffix = v.sha && v.sha !== 'dev' ? ` (${v.sha})` : '';
  return `v ${v.version}${shaSuffix} · ${dd}/${mm} ${hh}:${min}`;
}
